import { type ReactNode } from 'react'

import { Icon, IconButton, Pagination, Space } from '@procurify/ui'
import styled from 'styled-components'

import { configureColumn as col, DataTable } from '@webapp/components/DataTable'
import { type DatatableColumnConfig } from '@webapp/components/DataTable/types/DataTable.types'
import { useProcurifyIntl } from '@webapp/hooks'
import { type PODisclaimerLabel } from '@webapp/modules/purchasing/entities/PODisclaimerLabel'
import messages from './messages'

// Full-width override so the inline-edit cells fill the DataTable cell
// (ui-memory "Full-Width Components in DataTable Cells")
const FullWidthCellDecoratorStyled = styled.div`
  width: 100%;

  > div {
    display: block;
    width: 100% !important;
  }
`

export interface IPODisclaimerLabelTableProps {
  labels: PODisclaimerLabel[]
  defaultId: number | null
  isLoading: boolean
  page: number
  numPages: number
  onPrevPage: () => void
  onNextPage: () => void
  onToggleDefault?: (label: PODisclaimerLabel) => void
  onDeleteLabel?: (label: PODisclaimerLabel) => void
  renderEditableCells?: (label: PODisclaimerLabel) => {
    description: ReactNode
    text: ReactNode
  }
}

export const PODisclaimerLabelTable = ({
  labels,
  defaultId,
  isLoading,
  page,
  numPages,
  onPrevPage,
  onNextPage,
  onToggleDefault,
  onDeleteLabel,
  renderEditableCells,
}: IPODisclaimerLabelTableProps) => {
  const intl = useProcurifyIntl()

  const columns: DatatableColumnConfig<any, PODisclaimerLabel>[] = [
    col('BASIC', {
      label: intl.formatMessage(messages.columnLabelDescription),
      getter: (label) =>
        renderEditableCells
          ? renderEditableCells(label).description
          : label.description,
      decorate: (cell) => (
        <FullWidthCellDecoratorStyled>{cell}</FullWidthCellDecoratorStyled>
      ),
    }),
    col('BASIC', {
      label: intl.formatMessage(messages.columnLabelContent),
      getter: (label) =>
        renderEditableCells ? renderEditableCells(label).text : label.text,
      decorate: (cell) => (
        <FullWidthCellDecoratorStyled>{cell}</FullWidthCellDecoratorStyled>
      ),
    }),
    col('BASIC', {
      label: '',
      getter: (label) => {
        const isDefault = label.id === defaultId

        return (
          <Space direction='horizontal' size='small'>
            <IconButton
              aria-label={intl.formatMessage(
                isDefault
                  ? messages.removeDefaultButtonLabel
                  : messages.setDefaultButtonLabel,
                { description: label.description }
              )}
              htmlTitle={intl.formatMessage(messages.setDefaultButtonTitle)}
              iconComponent={
                <Icon
                  icon='star-solid'
                  color={isDefault ? 'warning' : 'lightGrey'}
                />
              }
              onClick={() => onToggleDefault?.(label)}
            />
            <IconButton
              icon='delete'
              aria-label={intl.formatMessage(messages.deleteLabelButtonLabel, {
                description: label.description,
              })}
              htmlTitle={intl.formatMessage(messages.deleteLabelButtonTitle)}
              onClick={() => onDeleteLabel?.(label)}
            />
          </Space>
        )
      },
    }),
  ]

  const handlePageChange = (newPage: number) => {
    if (newPage > page) {
      onNextPage()
    } else if (newPage < page) {
      onPrevPage()
    }
  }

  return (
    <Space>
      <DataTable<PODisclaimerLabel>
        id='po-disclaimer-label-table'
        aria-label={intl.formatMessage(messages.tableAriaLabel)}
        data={labels}
        isLoading={isLoading}
        columns={columns}
        customizableColumns={false}
        persistCustomizedColumns={false}
        emptyStateText={intl.formatMessage(messages.emptyStateText)}
        emptyStateSize='small'
      />
      <Space direction='horizontal' justify='end'>
        <Pagination
          currentPage={page}
          numPages={numPages}
          onPageChange={handlePageChange}
        />
      </Space>
    </Space>
  )
}

PODisclaimerLabelTable.displayName = 'PODisclaimerLabelTable'
